/**
 * /tools/subnet-calculator — public, anonymous.
 * Uses networkUtils.getSubnetInfo + COMMON_SUBNETS for live calculations.
 * Posts a fire-and-forget telemetry event to /api/tools/usage.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator, Copy, ArrowLeft, Sparkles } from "lucide-react";
import {
    getSubnetInfo, COMMON_SUBNETS, cidrToMask, maskToCIDR,
} from "../zeroconf/utils/networkUtils";
import { validateIPv4, formatIPInput } from "../zeroconf/utils/validation";
import { postJSON } from "../lib/api";
import { toast } from "sonner";
import { usePageMeta } from "../lib/usePageMeta";
import { ToolPageMeta } from "../components/ToolPageMeta";

// Anonymous session helper
function getAnonSessionId() {
    try {
        let id = localStorage.getItem("cf_anon_session");
        if (!id) {
            id = "anon-" + crypto.randomUUID();
            localStorage.setItem("cf_anon_session", id);
        }
        return id;
    } catch {
        return "anon-no-storage";
    }
}

function classOf(ip) {
    const first = Number((ip || "").split(".")[0]);
    if (Number.isNaN(first)) return "—";
    if (first < 128) return "A";
    if (first < 192) return "B";
    if (first < 224) return "C";
    if (first < 240) return "D (multicast)";
    return "E (experimental)";
}

function wildcardMask(mask) {
    if (!validateIPv4(mask)) return "—";
    return mask.split(".").map((o) => 255 - Number(o)).join(".");
}

const ResultRow = ({ label, value, mono = true, testId }) => (
    <div className="flex items-baseline justify-between py-2 border-b border-white/[0.04]">
        <span className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-text-muted">{label}</span>
        <span className={`text-[13.5px] text-text-primary ${mono ? "font-mono" : ""}`} data-testid={testId}>{value}</span>
    </div>
);

const FaqRow = ({ q, a }) => (
    <details className="rounded-md border border-white/[0.04] bg-surface/40 p-4 [&_summary::-webkit-details-marker]:hidden">
        <summary className="cursor-pointer text-[13.5px] font-semibold text-text-primary">{q}</summary>
        <p className="mt-3 text-[13px] text-text-muted leading-relaxed">{a}</p>
    </details>
);

export const SubnetCalculator = () => {
    const [ip, setIp] = useState("192.168.1.0");
    const [cidr, setCidr] = useState("24");
    const reported = useRef(false);

    usePageMeta({
        title: "Subnet Calculator — IPv4 network, broadcast, CIDR & hosts",
        description:
            "Free IPv4 subnet calculator: enter an IP and CIDR (or mask) to instantly see network address, broadcast, usable host range, total hosts, wildcard mask, and address class.",
    });

    const mask = useMemo(() => cidrToMask(Number(cidr) || 24), [cidr]);

    const result = useMemo(() => {
        if (!validateIPv4(ip)) return null;
        try { return getSubnetInfo(ip, mask); } catch { return null; }
    }, [ip, mask]);

    // Fire telemetry on first valid calculation (debounced via useRef)
    useEffect(() => {
        if (!result || reported.current) return;
        reported.current = true;
        postJSON("/tools/usage", {
            tool_id: "subnet-calculator",
            action: "calculate",
            anon_session_id: getAnonSessionId(),
            details: { ip, cidr: Number(cidr) },
        }).catch(() => {/* fire and forget */});
    }, [result, ip, cidr]);

    const copySummary = async () => {
        if (!result) return;
        const summary = [
            `Subnet Calculator — ${ip}/${cidr}`,
            `Network address:   ${result.network}`,
            `Broadcast address: ${result.broadcast}`,
            `First usable host: ${result.firstUsable}`,
            `Last usable host:  ${result.lastUsable}`,
            `Usable hosts:      ${result.usableHosts.toLocaleString()}`,
            `Subnet mask:       ${mask}`,
            `Wildcard mask:     ${wildcardMask(mask)}`,
            `Address class:     ${classOf(ip)}`,
            `\nGenerated at cybersecurityforefront.com/tools/subnet-calculator`,
        ].join("\n");
        try {
            await navigator.clipboard.writeText(summary);
            toast.success("Result copied to clipboard");
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div className="pt-24 sm:pt-32 pb-20 mx-auto max-w-5xl px-5" data-testid="subnet-calculator-page">
            <ToolPageMeta toolId="subnet-calculator" />
            <Link to="/tools" className="inline-flex items-center gap-1.5 text-[12.5px] text-text-muted hover:text-accent-cyan mb-6">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to tools
            </Link>

            <motion.header
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                className="mb-7"
            >
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Free tool</p>
                <h1 className="mt-2 font-display font-semibold text-3xl sm:text-4xl tracking-tight flex items-center gap-3">
                    <Calculator className="w-7 h-7 text-accent-cyan" />
                    Subnet calculator
                </h1>
                <p className="mt-3 text-[14px] text-text-muted max-w-2xl">
                    Enter an IPv4 address and a CIDR (or dotted-decimal mask) to instantly compute network address, broadcast, usable host range, total hosts, wildcard mask, and address class. Useful when sizing VLANs, planning DHCP scopes, or reading firewall ACLs.
                </p>
            </motion.header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Calculator card */}
                <section className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-6" data-testid="calculator-card">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-2">
                            <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">IPv4 address</label>
                            <input type="text" value={ip} onChange={(e) => setIp(formatIPInput(e.target.value))} placeholder="192.168.1.0" data-testid="subnet-ip"
                                className="w-full px-3.5 py-3 bg-canvas border border-white/[0.08] rounded-md text-[18px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan focus:shadow-glow-cyan transition-colors" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">CIDR</label>
                            <select value={cidr} onChange={(e) => setCidr(e.target.value)} data-testid="subnet-cidr"
                                className="w-full px-3.5 py-3 bg-canvas border border-white/[0.08] rounded-md text-[18px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan">
                                {Array.from({ length: 17 }, (_, i) => 16 + i).map((c) => (
                                    <option key={c} value={c}>/{c}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {result ? (
                        <>
                            <div className="mt-5 rounded-lg border border-accent-cyan/15 bg-accent-cyan/[0.03] p-4">
                                <ResultRow label="Network address"   value={result.network}      testId="result-network" />
                                <ResultRow label="Broadcast address" value={result.broadcast}    testId="result-broadcast" />
                                <ResultRow label="First usable host" value={result.firstUsable}  testId="result-first" />
                                <ResultRow label="Last usable host"  value={result.lastUsable}   testId="result-last" />
                                <ResultRow label="Usable hosts"      value={result.usableHosts.toLocaleString()} testId="result-hosts" />
                                <ResultRow label="Subnet mask"       value={mask}                testId="result-mask" />
                                <ResultRow label="Wildcard mask"     value={wildcardMask(mask)}  testId="result-wildcard" />
                                <ResultRow label="CIDR notation"     value={`/${result.cidr}`}   testId="result-cidr" />
                                <ResultRow label="Address class"     value={classOf(ip)} mono={false} testId="result-class" />
                            </div>

                            {/* Visual bar */}
                            <div className="mt-4">
                                <div className="flex items-stretch h-4 rounded overflow-hidden border border-white/[0.06]">
                                    <div className="bg-accent-cyan/60" style={{ width: "5%" }} title={`Network ${result.network}`} />
                                    <div className="bg-accent-mint/70" style={{ width: "90%" }} title={`Usable ${result.firstUsable}–${result.lastUsable}`} />
                                    <div className="bg-accent-amber/70" style={{ width: "5%" }} title={`Broadcast ${result.broadcast}`} />
                                </div>
                                <div className="mt-1.5 flex justify-between text-[10px] font-mono text-text-dim">
                                    <span>Network</span><span>Usable range ({result.usableHosts.toLocaleString()} hosts)</span><span>Broadcast</span>
                                </div>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-2">
                                <button onClick={copySummary} data-testid="subnet-copy"
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30">
                                    <Copy className="w-3.5 h-3.5" /> Copy result
                                </button>
                            </div>
                        </>
                    ) : (
                        <p className="mt-5 text-[12.5px] text-accent-amber font-mono">Enter a valid IPv4 address.</p>
                    )}
                </section>

                {/* Common subnets reference */}
                <aside className="rounded-2xl border border-white/[0.06] bg-surface/50 p-5" data-testid="common-subnets">
                    <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-3">Common subnets</p>
                    <ul className="space-y-1">
                        {COMMON_SUBNETS.map((s) => (
                            <li key={s.mask} className="flex justify-between py-1.5 px-2 text-[12px] font-mono hover:bg-accent-cyan/[0.04] rounded cursor-pointer transition-colors"
                                onClick={() => setCidr(String(s.cidr))}>
                                <span className="text-text-primary">/{s.cidr}</span>
                                <span className="text-text-muted">{s.mask}</span>
                                <span className="text-text-dim">{s.hosts.toLocaleString()} hosts</span>
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>

            {/* Soft signup CTA */}
            <div className="mt-10 rounded-xl border border-accent-cyan/15 bg-accent-cyan/[0.04] p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="font-display font-semibold text-[15px] flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-accent-cyan" /> Want to save your subnet plans?
                    </p>
                    <p className="text-[12.5px] text-text-muted mt-1">Sign up free to generate a complete VLAN topology with ZeroConf.</p>
                </div>
                <Link to="/signup" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan">
                    Sign up free
                </Link>
            </div>

            {/* FAQ section (SEO) */}
            <section className="mt-12">
                <h2 className="font-display font-semibold text-2xl tracking-tight mb-5">Subnetting FAQ</h2>
                <div className="space-y-2">
                    <FaqRow
                        q="What is a subnet mask?"
                        a="A subnet mask is a 32-bit value that divides an IPv4 address into a network portion and a host portion. The contiguous 1-bits at the start indicate the network; the 0-bits indicate hosts. A /24 mask (255.255.255.0) leaves 8 bits for hosts, allowing 254 usable addresses per subnet (256 minus network and broadcast)."
                    />
                    <FaqRow
                        q="How do I choose between /24 and /23?"
                        a="Use /24 (254 hosts) when each VLAN has at most a few hundred endpoints — typical for staff, servers, or IoT segments. Step up to /23 (510 hosts) when you genuinely need more addresses; never oversize for vanity. A poorly sized subnet wastes address space and complicates broadcast traffic. As a rule of thumb, target ≤ 60% utilization at peak so you have headroom for growth."
                    />
                    <FaqRow
                        q="Why does my calculator say 254 usable hosts on /24, not 256?"
                        a="The first address in any subnet is the network address (e.g., 192.168.1.0) and the last is the broadcast address (192.168.1.255). Neither can be assigned to a host, so a /24 yields 256 − 2 = 254 usable addresses. /31 and /32 are special cases used for point-to-point links and loopback assignments respectively."
                    />
                    <FaqRow
                        q="What's the difference between a subnet mask and a wildcard mask?"
                        a="A subnet mask flags the network bits as 1s (e.g., 255.255.255.0). A wildcard mask is its bitwise inverse (0.0.0.255) and is most often used in Cisco IOS ACLs and OSPF area definitions to specify which bits the device should ignore when matching. ZeroConf's generators emit the right form for each vendor automatically."
                    />
                </div>
            </section>
        </div>
    );
};

export default SubnetCalculator;
